// Common
import { ChangeDetectorRef, Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { Constants } from '@frontendlib/common/types/Constants';

// RxJS
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';

// Service
import { ConnectionsService } from '@modules/connections/services/connections.service';
import { KnotsService } from '@modules/knots/services/knots.service';
import { KnowledgePanelService } from '@modules/knowledge/services/knowledge-panel.service';
import { TagsService } from '@modules/tags/services/tags.service';

// Types
import { Stitch } from '@modules/common/types/stitch';
import { Connection } from '@modules/connections/types/connection';
import { Knot } from '@modules/knots/types/knot';
import type { KnotsManageListState } from '@modules/knots/types/knots-manage-list-state';
import { KnotsNeighboursFilters } from '@modules/knots/types/neighbours-filters';
import { StateKey } from '@modules/settings/types/state-key';
import { Tag } from '@modules/tags/types/tag';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { RelevanceKnot } from '@modules/knots/types/relevance-knot';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { resolveNeighborMarkers } from '@modules/knowledge/utils/resolve-neighbor-markers';

@Component({
  selector: 'app-knots-knowledge-section',
  templateUrl: './knots-section.component.html',
  styleUrls: ['./knots-section.component.less'],
  standalone: false,
  host: {
    class: 'flex h-full flex-col overflow-hidden px-8 py-4',
  },
})
export class KnowledgeKnotsSectionComponent implements OnInit, OnDestroy {
  private readonly filterIndicator = viewChild(ContextMenuStateIndicatorComponent);
  public StitchType = StitchType;
  public StateKey = StateKey;
  public state: KnotsManageListState;
  public neighborsKnots: Knot[] = [];
  public stitchItem: Stitch;
  public pagesCount = 0;
  /** How many related knots there are in all, as the server counts them. */
  public total = 0;
  public page = 0;
  public perPage = Constants.PAGE_SIZE;
  public knotsHistory: Knot[] = [];
  public tagsHistory: Tag[] = [];
  public connectionsHistory: Connection[] = [];
  public debug: 'createdAt' | 'source' | 'entityType' = null;
  protected loading = false;

  protected readonly defaultFiltersState: KnotsManageListState = {
    sort: {
      by: 'relevance',
      order: 'desc',
    },
    filters: {
      createdFrom: null,
      createdTo: null,
    },
  };

  private alive = new Subject<void>();
  private loadNeighbors = new Subject<void>();

  constructor(
    private kpService: KnowledgePanelService,
    private knotsService: KnotsService,
    private tagsService: TagsService,
    private connectionsService: ConnectionsService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  /**
   * Lifecycle
   */

  ngOnInit() {
    this.loadNeighbors
      .pipe(
        debounceTime(300),
        map(() => {
          const hasSomeHistory = this.knotsHistory.length || this.tagsHistory.length || this.connectionsHistory.length;
          // When no selection, pass empty arrays — the backend resolves all markers
          // from the source item (contextItems). This decouples the KP from form pagination.
          return [
            hasSomeHistory ? this.knotsHistory : [],
            hasSomeHistory ? this.tagsHistory : [],
            hasSomeHistory ? this.connectionsHistory : [],
          ] as const;
        }),
        switchMap(([knots, tags, connections]) => {
          if (!knots.length && !tags.length && !connections.length && !this.stitchItem?.id) {
            return of({ knots: [], count: 0 });
          }
          let afterKnot: RelevanceKnot | null = null;
          if (this.page > 0) {
            afterKnot = this.neighborsKnots.at(-1) as RelevanceKnot;
          }
          const config = { requestContext: owningTenantContext(this.stitchItem?.accountId) };
          const lookups = { knots: this.knotsService, tags: this.tagsService, connections: this.connectionsService };

          return resolveNeighborMarkers({ knots, tags, connections }, lookups, config).pipe(
            switchMap((markers) =>
              markers
                ? this.knotsService.getNeighbors(
                    {
                      ...KnotsNeighboursFilters.fromListState(this.state),
                      ...markers,
                      contextItems: this.stitchItem?.id ? [this.stitchItem.id] : [],
                      limit: this.perPage,
                      after: afterKnot,
                    },
                    config,
                  )
                : of({ knots: [], count: 0 }),
            ),
            // Caught per request so one failure doesn't stop the next selection loading (SBUX-1173).
            catchError(() => of({ knots: [], count: 0 })),
          );
        }),
        takeUntil(this.alive),
      )
      .subscribe(({ knots, count }) => {
        this.loading = false;
        this.pagesCount = Math.ceil(count / this.perPage);
        this.total = count;
        this.neighborsKnots = [
          ...this.neighborsKnots.slice(0, this.page * this.perPage),
          ...knots,
          ...this.neighborsKnots.slice((this.page + 1) * this.perPage, count),
        ].filter((i) => !!i);
      });

    // The CONTEXT item — what the 3rd panel has open — not this panel's own form item. It is the
    // sole input of the neighbour query when nothing is selected (`contextItems` below), so reading
    // the DP's `selectedStitchItem` here left the list empty for good once a DP form had been closed
    // and nulled it. See SBUX-750 / devdoc/specs/knowledge-panel-selection-scopes.md.
    this.kpService
      .getContextStitchItem()
      .pipe(takeUntil(this.alive))
      .subscribe((stitchItem) => {
        // Closing the item in the 3rd panel clears this row's sort & filter, as its × does, so the next item starts
        // from the default (Johanna, 2026-10-21: a filter set for one item stayed on after it closed).
        if (this.stitchItem && !stitchItem) {
          this.filterIndicator()?.reset();
        }
        this.stitchItem = stitchItem;
        // Refetch: the query is keyed on this item, so the reload must not depend on a history
        // emission happening to arrive with the item change.
        this.reloadNeighbors();
      });

    // Everything this panel's queries must honour. It lives on the service so all four lists in the
    // panel share it.
    this.kpService
      .getQueryFilter()
      .pipe(takeUntil(this.alive))
      .subscribe(({ connections, knots, tags }) => {
        this.knotsHistory = knots;
        this.tagsHistory = tags;
        this.connectionsHistory = connections;
        this.reloadNeighbors();
        this.changeDetector.detectChanges();
      });
  }

  ngOnDestroy() {
    this.kpService.setDeepMode('knots', false);
    this.loadNeighbors.complete();
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  handleClickItem(knot: Knot) {
    this.kpService.addKnotToSelection(knot, null, false);
  }

  /** The related knots not shown yet: the "+N more" pill's count. */
  get remaining(): number {
    return Math.max(0, this.total - (this.page + 1) * this.perPage);
  }

  showMore() {
    this.page = Math.min(this.page + 1, this.pagesCount - 1);
    if (!this.neighborsKnots[this.page * this.perPage]) {
      this.loading = true;
      this.loadNeighbors.next();
    }
  }

  showLess() {
    this.page = 0;
  }

  doDebug() {
    switch (this.debug) {
      case 'createdAt':
        this.debug = 'source';
        break;
      case 'source':
        this.debug = 'entityType';
        break;
      case 'entityType':
        this.debug = null;
        break;
      default:
        this.debug = 'createdAt';
    }
  }

  setState(state: KnotsManageListState) {
    this.state = state;
    this.kpService.setDeepMode('knots', !!state.filters?.deepMode);
    this.reloadNeighbors();
  }

  private reloadNeighbors(): void {
    this.page = 0;
    this.neighborsKnots = [];
    this.loading = true;
    this.loadNeighbors.next();
  }
}
